/**
 * FATE & GRÂCE — Invoices API Service
 *
 * GET    /api/invoices/             ?table_id=&date_from=&date_to= → Invoice[]
 * GET    /api/invoices/{id}/        → Invoice
 * GET    /api/invoices/{id}/pdf/    → PDF (download)
 * POST   /api/invoices/{id}/reprint/ → 200
 */
import { api, downloadFile, unwrap,  BASE_URL, getToken  } from "./client";
import { MOCK_INVOICES } from "../mock";
import { MOCK_USERS } from "../mock";   // ← déplacer depuis ligne ~55
import { MOCK_AUDIT } from "../mock";  

export const invoicesService = {
  async list(params = {}) {
    try {
      const qs = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v))
      ).toString();
      const data = await api.get(`/invoices/${qs ? "?" + qs : ""}`);
      return unwrap(data);
    } catch (err) {
      if (err.isNetwork) return [...MOCK_INVOICES];
      throw err;
    }
  },

  async get(id) {
    try {
      return await api.get(`/invoices/${id}/`);
    } catch (err) {
      if (err.isNetwork) return MOCK_INVOICES.find((i) => i.id === id);
      throw err;
    }
  },

  async downloadPdf(id, filename) {
    try {
      await downloadFile(`/invoices/${id}/pdf/`, filename || `facture-${id}.pdf`);
    } catch (err) {
      if (err.isNetwork) throw new Error("PDF indisponible en mode hors-ligne.");
      throw err;
    }
  },
  async printTicket(id) {
    const response = await fetch(`${BASE_URL}/invoices/${id}/pdf/`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    // Ouvre dans un iframe invisible → dialogue impression auto
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 2000);
    };
  },
};

/* ══════════════════════════════════════════════════════════
   USERS API Service
   ──────────────────────────────────────────────────────────
   GET    /api/users/               → User[]
   POST   /api/users/               { first_name, last_name, login, role } → User
   PUT    /api/users/{id}/          → User
   DELETE /api/users/{id}/          → 204
   POST   /api/users/{id}/toggle/   → User  (activer/désactiver)
   POST   /api/users/{id}/reset-password/ { new_password } → 200
   ══════════════════════════════════════════════════════════ */

export const usersService = {
  async list() {
    try {
      const data = await api.get("/users/");
      return unwrap(data);
    } catch (err) {
      if (err.isNetwork) return [...MOCK_USERS];
      throw err;
    }
  },

  async create(payload) {
    try {
      return await api.post("/users/", payload);
    } catch (err) {
      if (err.isNetwork) return { id: Date.now(), ...payload, isActive: true };
      throw err;
    }
  },

  async update(id, payload) {
    try {
      return await api.put(`/users/${id}/`, payload);
    } catch (err) {
      if (err.isNetwork) return { id, ...payload };
      throw err;
    }
  },

  async remove(id) {
    try {
      return await api.delete(`/users/${id}/`);
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  async toggle(id) {
    try {
      return await api.post(`/users/${id}/toggle/`);
    } catch (err) {
      if (err.isNetwork) return { id, toggled: true };
      throw err;
    }
  },

  async resetPassword(id, newPassword) {
    try {
      return await api.post(`/users/${id}/reset-password/`, { new_password: newPassword });
    } catch (err) {
      if (err.isNetwork) return { success: true };
      throw err;
    }
  },
};

/* ══════════════════════════════════════════════════════════
   AUDIT API Service
   ──────────────────────────────────────────────────────────
   GET    /api/audit-logs/          ?user=&action=&date_from=&date_to= → Log[]
   GET    /api/audit-logs/export/   ?format=csv|excel|txt              → file
   ══════════════════════════════════════════════════════════ */


export const auditService = {
  async list(params = {}) {
    try {
      const qs = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v))
      ).toString();
      const data = await api.get(`/audit-logs/${qs ? "?" + qs : ""}`);
      // Retourner la réponse paginée complète { count, next, previous, results }
      if (data && Array.isArray(data.results)) return data;
      // Fallback si pas de pagination
      return { count: Array.isArray(data) ? data.length : 0, next: null, previous: null, results: Array.isArray(data) ? data : [] };
    } catch (err) {
      if (err.isNetwork) return { count: MOCK_AUDIT.length, next: null, previous: null, results: [...MOCK_AUDIT] };
      throw err;
    }
  },

  async export(format = "csv") {
    try {
      await downloadFile(`/audit-logs/export/?fmt=${format}`, `audit-logs.${format}`);
    } catch (err) {
      if (err.isNetwork) throw new Error("Export indisponible en mode hors-ligne.");
      throw err;
    }
  },
};

/* ══════════════════════════════════════════════════════════
   REPORTS API Service
   ──────────────────────────────────────────────────────────
   GET    /api/reports/dashboard/   → DashboardStats
   GET    /api/reports/orders/      ?period=day|week|month → OrderStats
   GET    /api/reports/stock/       → StockStats
   GET    /api/reports/kpi/         ?period= → KPIData
   GET    /api/reports/export/      ?type=orders|stock&format=csv|excel|pdf → file
   ══════════════════════════════════════════════════════════ */

/**
 * Normalise un argument de période en query-string.
 * Accepte :
 *   - "month" | "30" | 30        → ?period=...
 *   - { period }                 → ?period=...
 *   - { date_from, date_to }     → plage explicite
 */
const periodQs = (p) => {
  const obj =
    typeof p === "object" && p !== null ? p : { period: p ?? "month" };
  const entries = Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== "");
  return new URLSearchParams(Object.fromEntries(entries)).toString();
};

export const reportsService = {
  async dashboard() {
    try {
      return await api.get("/reports/dashboard/");
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  async orders(period = "month") {
    try {
      const qs = periodQs(period);
      return await api.get(`/reports/orders/${qs ? "?" + qs : ""}`);
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  async stock(period = "month") {
    try {
      const qs = periodQs(period);
      return await api.get(`/reports/stock/${qs ? "?" + qs : ""}`);
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  async kpi(period = "month") {
    try {
      const qs = periodQs(period);
      return await api.get(`/reports/kpi/${qs ? "?" + qs : ""}`);
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  /**
   * Export d'un rapport.
   * @param {string} type   orders | stock | invoices | movements | kpi
   * @param {string} format csv | excel | pdf
   * @param {object} period { period?, date_from?, date_to? } (optionnel)
   */
  async export(type, format = "excel", period = {}) {
    try {
      // NB: on envoie 'fmt' et non 'format' — 'format' est intercepté par la
      // négociation de contenu de DRF et renvoie un 404.
      const params = { type, fmt: format, ...period };
      const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
      const qs = new URLSearchParams(Object.fromEntries(entries)).toString();
      const ext = format === "excel" ? "xlsx" : format;
      await downloadFile(`/reports/export/?${qs}`, `rapport-${type}.${ext}`);
    } catch (err) {
      if (err.isNetwork) throw new Error("Export indisponible en mode hors-ligne.");
      throw err;
    }
  },
};

/* ══════════════════════════════════════════════════════════
   NOTIFICATIONS API Service (WebSocket via polling fallback)
   ──────────────────────────────────────────────────────────
   GET    /api/notifications/       → Notification[]
   POST   /api/notifications/{id}/read/ → 200
   POST   /api/notifications/read-all/ → 200
   ══════════════════════════════════════════════════════════ */

export const notificationsService = {
  async list() {
    try {
      const data = await api.get("/notifications/");
      return unwrap(data);
    } catch (err) {
      if (err.isNetwork) return [];
      throw err;
    }
  },

  async markRead(id) {
    try {
      return await api.post(`/notifications/${id}/read/`);
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  async markAllRead() {
    try {
      return await api.post("/notifications/read-all/");
    } catch (err) {
      if (err.isNetwork) return null;
      throw err;
    }
  },

  /**
   * WebSocket connection pour notifications temps réel.
   * Retourne une fonction de cleanup.
   * @param {function} onMessage - callback({ type, data })
   */
  connectWebSocket(onMessage) {
    const wsUrl = (process.env.REACT_APP_WS_URL || "ws://localhost:8000/ws") + "/notifications/";
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => console.log("[WS] Connecté aux notifications");
      ws.onmessage = (e) => {
        try { onMessage(JSON.parse(e.data)); } catch (_) {}
      };
      ws.onerror = () => console.warn("[WS] WebSocket indisponible — mode polling");
      ws.onclose = () => console.log("[WS] Déconnecté");
    } catch (_) {
      console.warn("[WS] WebSocket non supporté");
    }
    return () => ws && ws.close();
  },
  /**
   * S'abonner aux Push Notifications (Web Push / VAPID).
   * À appeler après que l'utilisateur a accordé la permission.
   * La clé publique VAPID doit être dans .env :
   *   REACT_APP_VAPID_PUBLIC_KEY=votre_cle_publique
   */
  async subscribePush() {
    console.log("On est dans subscribePush,");
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.warn("[Push] Non supporté sur ce navigateur");
      return null;
    }
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    
    if (!vapidKey) {
      console.warn("[Push] REACT_APP_VAPID_PUBLIC_KEY manquante dans .env");
      return null;
    }
 
    try {
      // Convertir la clé VAPID base64url → Uint8Array
      const padding  = "=".repeat((4 - (vapidKey.length % 4)) % 4);
      const base64   = (vapidKey + padding).replace(/-/g, "+").replace(/_/g, "/");
      const rawData  = window.atob(base64);
      const appKey   = Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));

      console.log("Clé VAPID convertie:", appKey);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: appKey,
      });
      console.log("Abonnement Push obtenu:", sub);
      // Extraire les clés de l'abonnement
      const p256dh = btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh"))));
      const auth   = btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth"))));
      
      // Envoyer l'abonnement au backend Django
      await api.post("/notifications/push-subscribe/", {
        endpoint: sub.endpoint,
        p256dh,
        auth,
      });
 
      console.log("[Push] Abonnement enregistré ✅");
      return sub;
    } catch (err) {
      // L'utilisateur a refusé ou une erreur technique — on n'interrompt pas l'app
      console.warn("[Push] Abonnement échoué:", err.message);
      return null;
    }
  },
};

/* ── Central export ─────────────────────────────────────── */
export * from "./auth";
export * from "./tables";
export * from "./orders";
export * from "./stock";
